/**
 * Module identity for the viewer half of the panorama-tour plugin.
 *
 * Single source of truth for the values the viewer reads when deciding
 * whether to surface panorama-bubble layer rendering. Backed by Vite's
 * `import.meta.env` so a build/runtime override cleanly turns the
 * module off without removing source.
 *
 * Companion to:
 *   - app-geoconverter/backend/app/services/panorama/__manifest__.py
 *   - app-geoconverter/frontend/src/routes/tour-project/manifest.ts
 *
 * All three carry the same MODULE_ID + MODULE_VERSION so the platform
 * can correlate the three halves of the plugin at deploy time.
 *
 * When ``isPanoramaTourEnabled()`` returns false:
 *   - <PanoramaBubbleView> is not mounted (catalog still contains the
 *     layer; it just isn't rendered as a 360-bubble overlay).
 *   - <StationMarkers> is not mounted in the Giro3D scene.
 *   - The scene-tree click → bubble entry path is short-circuited.
 *   - The minimap overlay is not rendered.
 * The viewer continues to build, render the 3D scene, point clouds,
 * 3D Tiles, etc. The bubble pipeline is the only thing disabled.
 */

export const MODULE_ID = "panorama-tour" as const;
export const MODULE_VERSION = "1.0.0" as const;

/**
 * Wire-format schema versions this module's renderer understands. The
 * handoff package's `kind` discriminator carries one of these values
 * on every stations.json. When v2 ships we append the new value here
 * while keeping v1 supported for backwards compatibility.
 */
export const SCHEMA_VERSIONS = ["panorama-bubble-v1"] as const;
export type SupportedSchemaVersion = (typeof SCHEMA_VERSIONS)[number];

/**
 * Vite env var that disables the entire panorama-bubble pipeline at
 * build/runtime. Default behaviour is ENABLED. Set to "0" / "false"
 * / "off" / "no" to skip.
 */
export const FEATURE_FLAG_ENV = "VITE_FEATURE_PANORAMA_TOUR" as const;

const DISABLED_TOKENS = new Set(["0", "false", "off", "no", ""]);

/**
 * Read the feature flag at call time so tests / Storybook stories can
 * flip the env without restarting Vite. Default ON; only the
 * documented disable tokens return false.
 */
export function isPanoramaTourEnabled(): boolean {
  const raw =
    (import.meta.env?.[FEATURE_FLAG_ENV] as string | undefined) ?? "1";
  return !DISABLED_TOKENS.has(raw.trim().toLowerCase());
}
